import { useEffect, useRef, useState } from 'react';
import { ArrowPathIcon, DocumentArrowDownIcon, ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { DEPRECATED_PERMISSION_CHILDREN, enumPermissionChildren, enumPermissions, normalizeLegacyPermissionParents } from '../../consts/management/users/users';
import { getPermissionCatalog } from '../Sidebar/const';
import { DEPARTMENTS, NON_ROUTE_PERMISSION_CATALOG } from '../../consts/organization/departments';
import { OverlayGrayBackground } from '../OverlayGrayBackground';

// Helpers: merge incoming permissions with defaults so all tabs/children exist.
// Keeps default ordering while overlaying incoming values where present.
const permissionsArrayToMap = (list) => {
    const map = new Map();
    (list || []).forEach((group) => {
        const [tabKey, children] = Object.entries(group || {})[0] || [];
        if (!tabKey) return;
        const childMap = new Map();
        (children || []).forEach((child) => {
            const [childKey, value] = Object.entries(child || {})[0] || [];
            if (!childKey) return;
            childMap.set(childKey, value);
        });
        map.set(tabKey, childMap);
    });
    return map;
};

const mapToPermissionsArray = (map, orderFrom) => {
    const result = [];
    const orderedTabs = [];
    (orderFrom || []).forEach((group) => {
        const [tabKey] = Object.entries(group || {})[0] || [];
        if (!tabKey) return;
        orderedTabs.push(tabKey);
    });
    const allTabs = [...new Set([...orderedTabs, ...Array.from((map || new Map()).keys())])];
    allTabs.forEach((tabKey) => {
        const childMap = map.get(tabKey) || new Map();
        const defaultGroup = (orderFrom || []).find((g) => Object.keys(g || {})[0] === tabKey);
        const defaultChildren = defaultGroup ? Object.values(defaultGroup)[0] : [];
        const orderedChildKeys = (defaultChildren || []).map((c) => Object.keys(c)[0]);
        const allChildKeys = [...new Set([...orderedChildKeys, ...Array.from(childMap.keys())])];
        const childrenArr = allChildKeys.map((childKey) => ({ [childKey]: childMap.get(childKey) }));
        result.push({ [tabKey]: childrenArr });
    });
    return result;
};

// Route-derived permissions plus the synthetic ones (Governance) that have no Eadin page.
const permissionCatalog = [...getPermissionCatalog(), ...NON_ROUTE_PERMISSION_CATALOG];
const permissionCatalogById = new Map(permissionCatalog.map((entry) => [entry.permissionId, entry]));
const supersededPermissionIds = new Set([
    'demand-planning',
    'open-to-buy',
    'demand-destribution-profile',
    'effective-capital-summary',
]);
const legacyPermissionParentOverrides = {
    'forecast-planning-system': 'merchandising',
};
const sectionLabels = {
    utilities: 'SYSTEM',
    governance: 'GOVERNANCE',
};
const defaultPermissions = permissionCatalog.reduce((groups, entry) => {
    const group = groups.find((item) => Object.prototype.hasOwnProperty.call(item, entry.parentId));
    if (group) {
        group[entry.parentId].push({ [entry.permissionId]: 'none' });
    } else {
        groups.push({ [entry.parentId]: [{ [entry.permissionId]: 'none' }] });
    }
    return groups;
}, []);

export const mergePermissionsWithDefaults = (defaults, incoming) => {
    const defaultMap = permissionsArrayToMap(defaults);
    const incomingMap = permissionsArrayToMap(incoming);
    const merged = new Map();
    const tabKeys = new Set([...(defaultMap?.keys?.() ? Array.from(defaultMap.keys()) : []), ...(incomingMap?.keys?.() ? Array.from(incomingMap.keys()) : [])]);
    tabKeys.forEach((tabKey) => {
        const defaultChildMap = defaultMap.get(tabKey) || new Map();
        const incomingChildMap = incomingMap.get(tabKey) || new Map();
        const childKeys = new Set([...(defaultChildMap?.keys?.() ? Array.from(defaultChildMap.keys()) : []), ...(incomingChildMap?.keys?.() ? Array.from(incomingChildMap.keys()) : [])]);
        const mergedChild = new Map();
        childKeys.forEach((childKey) => {
            const value = incomingChildMap.has(childKey) ? incomingChildMap.get(childKey) : defaultChildMap.get(childKey);
            mergedChild.set(childKey, value);
        });
        merged.set(tabKey, mergedChild);
    });
    return mapToPermissionsArray(merged, defaults);
};

export const updatePermissionsForIds = (allPermissions, permissionIds, value) =>
    (allPermissions || []).map((group) => {
        const [groupKey, children] = Object.entries(group || {})[0] || [];
        return {
            [groupKey]: (children || []).map((child) => {
                const [childKey] = Object.entries(child || {})[0] || [];
                return permissionIds.has(childKey) ? { [childKey]: value } : child;
            }),
        };
    });

const getDisplayPermissions = (allPermissions) => {
    const groups = new Map();
    const seenPermissionIds = new Set();

    (allPermissions || []).forEach((group) => {
        const [storedParentId, children] = Object.entries(group || {})[0] || [];
        (children || []).forEach((child) => {
            const [permissionId, access] = Object.entries(child || {})[0] || [];
            if (
                !permissionId ||
                seenPermissionIds.has(permissionId) ||
                supersededPermissionIds.has(permissionId) ||
                DEPRECATED_PERMISSION_CHILDREN.includes(permissionId) ||
                permissionId === 'vendor-settings'
            ) {
                return;
            }

            const catalogEntry = permissionCatalogById.get(permissionId);
            const parentId =
                catalogEntry?.parentId ||
                legacyPermissionParentOverrides[permissionId] ||
                storedParentId;
            if (!parentId) return;

            if (!groups.has(parentId)) groups.set(parentId, []);
            groups.get(parentId).push({ [permissionId]: access });
            seenPermissionIds.add(permissionId);
        });
    });

    return Array.from(groups.entries())
        .filter(([, children]) => children.length > 0)
        .map(([parentId, children]) => ({ [parentId]: children }));
};

export const RoleDrawer = ({ 
    type,
    isOpen, 
    isLoading,
    onClose, 
    onSuccessSubmit, 
    role 
}) => {
    const [roleName, setRoleName] = useState('');
    const [description, setDescription] = useState('');
    const [department, setDepartment] = useState('');
    const [companyWideGovernance, setCompanyWideGovernance] = useState(false);
    const [permissions, setPermissions] = useState(null);
    const [showUnsavedChangesWarning, setShowUnsavedChangesWarning] = useState(false);
    const baselineRef = useRef(null);

    useEffect(() => {
        if (!baselineRef.current) return;
        setShowUnsavedChangesWarning(
            JSON.stringify({ roleName, description, department, companyWideGovernance, permissions }) !== baselineRef.current
        );
    }, [roleName, description, department, companyWideGovernance, permissions]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (!isOpen) {
            setRoleName('');
            setDescription('');
            setDepartment('');
            setCompanyWideGovernance(false);
            setPermissions(null);
            setShowUnsavedChangesWarning(false);
            baselineRef.current = null;
        }
        // Disable scroll when drawer is open
        if (isOpen) {
            setRoleName(role?.name || '');
            setDescription(role?.description || '');
            setDepartment(role?.department || '');
            setCompanyWideGovernance(role?.companyWideGovernance === true);
            // Build a full permission set from defaults, overlaying any provided role permissions.
            const merged = mergePermissionsWithDefaults(defaultPermissions, normalizeLegacyPermissionParents(role?.permissions));
            setPermissions(merged);
            baselineRef.current = JSON.stringify({
                roleName: role?.name || '',
                description: role?.description || '',
                department: role?.department || '',
                companyWideGovernance: role?.companyWideGovernance === true,
                permissions: merged,
            });
            setShowUnsavedChangesWarning(false);
            document.body.style.overflow = 'hidden';
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.body.style.overflow = 'auto';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose, role]);

    const handleOnClose = () => {
        onClose?.();
        setRoleName('');
        setDescription('');
        setDepartment('');
        setCompanyWideGovernance(false);
        setPermissions(null);
        setShowUnsavedChangesWarning(false);
        baselineRef.current = null;
    }

    const handleGrantAll = (value) => {
        setPermissions((prev) => {
            const visiblePermissionIds = new Set(
                getDisplayPermissions(prev).flatMap((group) =>
                    Object.values(group || {})[0].map((child) => Object.keys(child)[0])
                )
            );
            return (prev || []).map((group) => {
                const [gKey, gChildren] = Object.entries(group || {})[0] || [];
                const updatedChildren = (gChildren || []).map((child) => {
                    const [cKey] = Object.entries(child || {})[0] || [];
                    return visiblePermissionIds.has(cKey) ? { [cKey]: value } : child;
                });
                return { [gKey]: updatedChildren };
            });
        });
    };

    const handleToggleAll = (children, value) => {
        const visibleChildKeys = new Set(
            (children || []).map((item) => Object.keys(item || {})[0])
        );
        setPermissions((prev) => updatePermissionsForIds(prev, visibleChildKeys, value));
    };

    const handleToggleChild = (childKey, value) => {
        setPermissions((prev) => updatePermissionsForIds(prev, new Set([childKey]), value));
    };

    if (!isOpen) return null;

    return (
        <>
            <div
                className={`fixed right-0 top-14 bottom-0 w-full max-w-xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 flex flex-col md:inset-y-0`}
            >
                <div className='border-border border-b p-4 pb-2 md:p-6 md:pb-2'>
                    <div className='flex flex-col mb-0 gap-4'>
                        <div className='flex items-center justify-between'>
                            <h2 className="text-lg font-semibold">
                                {type === "create" ? "Create Role": "Edit Role"}
                            </h2>
                            <button 
                                onClick={handleOnClose}
                                aria-label="Close role permissions editor"
                                title="Discard changes and close"
                                className='inline-flex h-9 w-9 items-center justify-center whitespace-nowrap rounded-md border border-gray-300 text-sm font-medium transition-all hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50 md:h-8 md:w-auto md:rounded-md md:border-0 md:px-3'
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>
                        {showUnsavedChangesWarning && (
                            <div className='flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-0'>
                                <ExclamationTriangleIcon className='h-5 w-5 text-amber-800'/>
                                <span className='text-amber-800'>You have unsaved changes</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className='flex-1 overflow-y-auto overflow-x-hidden relative'>
                    {isLoading && <OverlayGrayBackground />}
                    <div className='flex flex-col gap-4 border-b border-border p-4 md:p-6'>
                        <div className='flex flex-col gap-4'>
                            <div className='space-y-2'>
                                <label className='flex items-center gap-2 text-sm leading-none font-medium select-none' htmlFor='roleName'>
                                    Role Name *
                                </label>
                                <input
                                    id="roleName"
                                    value={roleName}
                                    onChange={(e) => {
                                        setRoleName(e.target.value);
                                        setShowUnsavedChangesWarning(true);
                                    }}
                                    className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                                />
                            </div>
                            {/* Department (where the role belongs) and governance breadth (where its
                                access applies) sit on one row; the Governance permission below
                                still decides what it may do. */}
                            <div className='flex flex-col gap-4 md:flex-row md:items-start'>
                                <div className='space-y-2 md:flex-1'>
                                    <label className='flex items-center gap-2 text-sm leading-none font-medium select-none' htmlFor='department'>
                                        Department *
                                    </label>
                                    <select
                                        id="department"
                                        value={department}
                                        onChange={(e) => {
                                            setDepartment(e.target.value);
                                            setShowUnsavedChangesWarning(true);
                                        }}
                                        className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                                    >
                                        <option value="">Select a department</option>
                                        {DEPARTMENTS.map((item) => (
                                            <option key={item.value} value={item.value}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </select>
                                    {!department && (
                                        <p className='text-xs text-gray-500'>
                                            A department is required before this role can be saved. It is the organizational scope KIP uses for governance and memory.
                                        </p>
                                    )}
                                </div>
                                <div className='space-y-2 md:w-72'>
                                    <span className='flex items-center gap-2 text-sm leading-none font-medium select-none'>
                                        Company-Wide Governance Access
                                    </span>
                                    <div className='flex h-9 items-center gap-3'>
                                        <button
                                            type="button"
                                            id="companyWideGovernance"
                                            role="switch"
                                            aria-checked={companyWideGovernance}
                                            aria-label="Company-Wide Governance Access"
                                            onClick={() => {
                                                setCompanyWideGovernance((prev) => !prev);
                                                setShowUnsavedChangesWarning(true);
                                            }}
                                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors outline-none ${companyWideGovernance ? 'bg-black' : 'bg-gray-300'}`}
                                        >
                                            <span
                                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${companyWideGovernance ? 'translate-x-5' : 'translate-x-0.5'}`}
                                            />
                                        </button>
                                        <span className='text-sm text-gray-600'>
                                            {companyWideGovernance ? 'All departments' : 'This department only'}
                                        </span>
                                    </div>
                                    <p className='text-xs text-gray-500'>
                                        Widens governance scope to every department. It grants no authority on its own — the Governance permission below still controls what this role can do.
                                    </p>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-sm leading-none font-medium select-none" htmlFor="description">
                                    Description
                                </label>
                                <textarea
                                    id="description"
                                    value={description}
                                    onChange={(e) => {
                                        setDescription(e.target.value);
                                        setShowUnsavedChangesWarning(true);
                                    }}
                                    className="border-input flex min-h-[100px] w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                                />
                            </div>
                        </div>        
                    </div>
                    <div className="border-b border-border p-4 md:p-6">
                        <div className='mb-0 flex flex-wrap items-center justify-between gap-2'>
                            <h3 className='font-medium'>
                                Permissions
                            </h3>
                            <div className='flex flex-wrap items-center gap-2'>
                                <button
                                    onClick={() => handleGrantAll('view')}
                                    className='inline-flex h-8 items-center justify-center whitespace-nowrap rounded-md border border-gray-300 bg-white px-2 text-[11px] font-medium shadow-xs transition-all hover:bg-primary hover:text-white disabled:pointer-events-none disabled:opacity-50 md:h-7 md:rounded-md md:text-xs'
                                >
                                    Grant all View
                                </button>
                                <button
                                    onClick={() => handleGrantAll('edit')}
                                    className='inline-flex h-8 items-center justify-center whitespace-nowrap rounded-md border border-gray-300 bg-white px-2 text-[11px] font-medium shadow-xs transition-all hover:bg-primary hover:text-white disabled:pointer-events-none disabled:opacity-50 md:h-7 md:rounded-md md:text-xs'
                                >
                                    Grant all Edit
                                </button>
                                <button
                                    onClick={() => handleGrantAll('none')}
                                    className='inline-flex h-8 items-center justify-center whitespace-nowrap rounded-md border border-gray-300 bg-white px-2 text-[11px] font-medium shadow-xs transition-all hover:bg-primary hover:text-white disabled:pointer-events-none disabled:opacity-50 md:h-7 md:rounded-md md:text-xs'
                                >
                                    Clear all
                                </button>
                            </div>
                        </div>
                        {/* <div className="relative flex-1">
                            <MagnifyingGlassIcon className="lucide lucide-search absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"/>
                            <input
                                placeholder="Search tabs..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] pl-10"
                            />
                        </div> */}
                    </div>
                    <div className='space-y-6 p-4 md:p-6'>
                        {getDisplayPermissions(permissions).map((permissionGroup, groupIndex) => {
                            const [tabKey, children] = Object.entries(permissionGroup || {})[0] || [];
                            if (!tabKey) return null;

                            return (
                                <div key={`tab-${tabKey}-${groupIndex}`}>
                                    <div className='mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
                                        <div className='flex items-center gap-2'>
                                            <h4 className='font-semibold text-primary'>
                                                {sectionLabels[tabKey] || enumPermissions[tabKey] || tabKey}
                                            </h4>
                                            <span className='inline-flex items-center justify-center rounded-md border px-2 py-0.5 font-medium w-fit whitespace-nowrap shrink-0 gap-1 overflow-hidden text-primary text-xs'>
                                                {(children || []).length} views
                                            </span>
                                        </div>  
                                        <div className='flex flex-wrap items-center gap-1'>
                                            <button
                                                onClick={() => handleToggleAll(children, 'view')}
                                                className='inline-flex h-7 items-center justify-center whitespace-nowrap rounded-md px-1.5 text-[11px] font-medium transition-all hover:bg-primary hover:text-white disabled:pointer-events-none disabled:opacity-50 md:h-6 md:rounded-md'
                                            >
                                                Select all View
                                            </button>
                                            <button
                                                onClick={() => handleToggleAll(children, 'edit')}
                                                className='inline-flex h-7 items-center justify-center whitespace-nowrap rounded-md px-1.5 text-[11px] font-medium transition-all hover:bg-primary hover:text-white disabled:pointer-events-none disabled:opacity-50 md:h-6 md:rounded-md'
                                            >
                                                Select all Edit
                                            </button>
                                            <button
                                                onClick={() => handleToggleAll(children, 'none')}
                                                className='inline-flex h-7 items-center justify-center whitespace-nowrap rounded-md px-1.5 text-[11px] font-medium transition-all hover:bg-primary hover:text-white disabled:pointer-events-none disabled:opacity-50 md:h-6 md:rounded-md'
                                            >
                                                Clear all
                                            </button>
                                        </div>
                                    </div>
                                    <div className='space-y-2'>
                                        {(children || []).map((childPermission, childIndex) => {
                                            const [childKey, access] = Object.entries(childPermission || {})[0] || [];
                                            if (!childKey) return null;
                                            const catalogEntry = permissionCatalogById.get(childKey);
                                            const childLabel =
                                                catalogEntry?.label ||
                                                enumPermissionChildren[childKey] ||
                                                childKey;
                                            return (
                                                <div key={`child-${tabKey}-${childKey}-${childIndex}`} className='flex items-center justify-between gap-2 border-b border-border p-1.5 md:p-2'>
                                                    <div
                                                        className='flex-1 min-w-0'
                                                        style={{ paddingLeft: `${Math.min(catalogEntry?.modulePath?.length || 0, 3) * 16}px` }}
                                                    >
                                                        <div className='truncate text-sm font-semibold'>
                                                            {childLabel}
                                                        </div>
                                                        {catalogEntry?.modulePath?.length > 0 && (
                                                            <div className='text-xs text-gray-500 truncate'>
                                                                {catalogEntry.modulePath.join(' / ')}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className='flex shrink-0 items-center overflow-hidden rounded-md border border-gray-300'>
                                                        <button
                                                            aria-label={`Set ${childLabel} access to none`}
                                                            onClick={() => handleToggleChild(childKey, 'none')}
                                                            className={`px-2 py-1 text-[11px] ${access === 'none' ? 'bg-gray-200 text-gray-700' : 'bg-white hover:bg-gray-100'}`}
                                                        >
                                                            None
                                                        </button>
                                                        <button
                                                            aria-label={`Set ${childLabel} access to view`}
                                                            onClick={() => handleToggleChild(childKey, 'view')}
                                                            className={`border-l border-gray-300 px-2 py-1 text-[11px] ${access === 'view' ? 'bg-gray-200 text-gray-700 md:bg-primary md:text-white' : 'bg-white hover:bg-gray-100'}`}
                                                        >
                                                            View
                                                        </button>
                                                        <button
                                                            aria-label={`Set ${childLabel} access to edit`}
                                                            onClick={() => handleToggleChild(childKey, 'edit')}
                                                            className={`border-l border-gray-300 px-2 py-1 text-[11px] ${access === 'edit' ? 'bg-gray-200 text-gray-700 md:bg-primary md:text-white' : 'bg-white hover:bg-gray-100'}`}
                                                        >
                                                            Edit
                                                        </button>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
                <div className='sticky bottom-0 z-10 border-t border-border bg-white p-4 md:p-6'>
                    <div className='flex gap-2'>
                        <button 
                            disabled={!department}
                            title={!department ? 'Select a department to save this role' : undefined}
                            onClick={() => {
                                if (!department) return;
                                if (
                                    showUnsavedChangesWarning
                                ) {
                                    onSuccessSubmit?.({
                                        roleId: role.id,
                                        roleName,
                                        description,
                                        department,
                                        companyWideGovernance,
                                        permissions
                                    });
                                }
                            }}
                            className='inline-flex h-10 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-gray-900 text-xs font-medium text-white transition-all hover:bg-gray-800 disabled:pointer-events-none disabled:opacity-50 md:h-8 md:rounded-md md:bg-primary'
                        >
                            <DocumentArrowDownIcon className='h-5 w-5 text-white' />
                            <span>
                                Save
                            </span>
                        </button>
                        <button
                            onClick={() => {
                                handleOnClose();
                            }}
                            className='group inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium transition-all hover:bg-gray-200 disabled:pointer-events-none disabled:opacity-50 md:h-8 md:rounded-md'
                        >
                            <ArrowPathIcon className='h-5 w-5 group-hover:text-white' />
                            <span className='group-hover:text-white'>
                                Discard
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};